import { html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";

import type { TextSize, FieldSize } from "@/internals/size";

import { WithHelperTextMixin, type WithHelperTextProps } from "@/internals/mixins/with-helper-text-mixin";
import { WithPopoverMixin, type WithPopoverProps } from "@/internals/mixins/with-popover-mixin";

import FormElement, { type FormElementProps } from "@/components/form/form-element";

import "@/components/display/atlas-icon-button/atlas-icon-button";
import "@/components/display/atlas-text/atlas-text";
import "@/components/form/atlas-helper-text/atlas-helper-text";

export type FormControlProps = FormElementProps &
    WithPopoverProps &
    WithHelperTextProps & {
        "label": string;
        "size": FieldSize;
        "hide-optional": boolean;
    };

const ComposedClass = WithPopoverMixin(WithHelperTextMixin(FormElement));

/**
 * Componente base para campos de formulário.
 */
export default class FormControl extends ComposedClass {
    /** A label que ficará acima do campo determinando o nome do mesmo */
    @property({ type: String }) label = "";

    /** O tamanho (altura e largura) do campo */
    @property({ type: String }) size: FieldSize = "md";

    /** O texto auxiliar que pode ser utilizado no campo */
    @property({ type: String, attribute: "helper-text" }) helperText = "";

    /** Indica se o texto (Opcional) deve ser oculto da label */
    @property({ type: Boolean, attribute: "hide-optional" }) hideOptional: boolean;

    protected renderLabel() {
        return when(
            this.label !== "",
            () => html`
                <label for="${this.id}" class="form-label">
                    <atlas-text size="${this.getLabelSize()}">${this.label} ${this.renderOptional()}</atlas-text>
                    ${this.renderPopoverInfoIcon()}
                </label>
            `
        );
    }

    private getLabelSize(): TextSize {
        const labelSizeForText = {
            "lg": "md",
            "md": "sm",
            "sm": "xs"
        };

        return (labelSizeForText[this.size] || "sm") as TextSize;
    }

    private renderOptional() {
        return when(
            !this.required && !this.hideOptional,
            () => html`<span class="form-label-optional">(Opcional)</span>`
        );
    }
}
